import { commandBarOverflowAriaLabel } from './FormsCommandBar.locstring.json';
import { observer } from 'mobx-react-lite';
import { CommandBarButton, IButtonProps } from '@fluentui/react/lib/Button';
import { ThemeProvider } from '@fluentui/react/lib/utilities/ThemeProvider';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { getOptimalTextColorString, themeFromColorScheme } from 'owa-color-utils/lib/colorUtils';
import loc from 'owa-localize';
import { getPalette } from 'owa-theme';
import type { OwaPalette } from 'owa-theme-shared';
import * as React from 'react';
import FilterableCommandBar, {
    FilterableCommandBarProps,
} from 'owa-filterable-menu/lib/components/FilterableCommandBar';
import { CALENDAR_ELLIPSES_ID } from '../index';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';

export interface FormsCommandBarProps extends FilterableCommandBarProps {
    colorScheme: ColorScheme;
}

export interface FormsCommandBarButtonProps extends IButtonProps {
    colorScheme: ColorScheme;
}

/**
 * Standard Forms command bar variant used in Reading Pane, Compose, and Peek
 */
const FormsCommandBar = observer(function (props: FormsCommandBarProps) {
    const { colorScheme } = props;

    const CustomizedFormsCommandBarButton = (props: IButtonProps) => (
        <FormsCommandBarButton colorScheme={colorScheme} {...props} />
    );

    const overflowLabel = loc(commandBarOverflowAriaLabel);

    const CustomizedOverflowButton = (props: IButtonProps) => (
        <TooltipHost content={overflowLabel}>
            <FormsCommandBarButton colorScheme={colorScheme} {...props} />
        </TooltipHost>
    );

    return (
        <ThemeProvider theme={colorScheme ? themeFromColorScheme(colorScheme) : undefined}>
            <FilterableCommandBar
                buttonAs={CustomizedFormsCommandBarButton}
                overflowButtonAs={CustomizedOverflowButton}
                overflowButtonProps={{
                    id: CALENDAR_ELLIPSES_ID,
                    ariaLabel: overflowLabel,
                }}
                styles={{
                    root: {
                        background: colorScheme.calLighterAlt,
                    },
                }}
                {...props}
            />
        </ThemeProvider>
    );
});

const FormsCommandBarButton = observer(function (props: FormsCommandBarButtonProps) {
    const {
        rootColor,
        pressedColor,
        rootBgColor,
        rootHoveredBgColor,
        rootExpandedBgColor,
        rootPressedBgColor,
    } = props.colorScheme
        ? getColorSchemeFromProps(props.colorScheme)
        : getColorSchemeFromTheme(getPalette());

    const splitButtonMenuButtonSelectors = {
        ':hover': {
            color: pressedColor,
            backgroundColor: rootHoveredBgColor,
        },
        ':active': {
            color: pressedColor,
            backgroundColor: rootHoveredBgColor,
        },
        '.ms-Button-menuIcon': {
            color: rootColor,
            selectors: {
                ':hover': {
                    color: pressedColor,
                },
                ':active': {
                    color: pressedColor,
                },
            },
        },
    };

    return (
        <CommandBarButton
            {...props}
            styles={{
                ...props.styles,
                root: {
                    color: rootColor,
                    backgroundColor: rootBgColor,
                    borderRadius: props.split ? '2px 0px 0px 2px' : 2,
                    height: 32,
                    margin: props.split ? '6px 0px' : '6px 3px',
                },

                rootHovered: {
                    color: pressedColor,
                    backgroundColor: rootHoveredBgColor,
                },

                rootPressed: {
                    color: pressedColor,
                    backgroundColor: rootPressedBgColor,
                },

                rootExpanded: {
                    color: pressedColor,
                    backgroundColor: rootExpandedBgColor,
                },

                rootExpandedHovered: {
                    color: pressedColor,
                    backgroundColor: rootExpandedBgColor,
                },

                icon: {
                    color: rootColor,
                },

                iconHovered: {
                    color: pressedColor,
                },

                iconPressed: {
                    color: pressedColor,
                },

                iconExpanded: {
                    color: pressedColor,
                },

                iconExpandedHovered: {
                    color: pressedColor,
                },

                menuIcon: {
                    color: rootColor,
                },

                menuIconHovered: {
                    color: pressedColor,
                },

                menuIconPressed: {
                    color: pressedColor,
                },

                menuIconExpanded: {
                    color: pressedColor,
                },

                menuIconExpandedHovered: {
                    color: pressedColor,
                },

                splitButtonMenuButton: {
                    color: rootColor,
                    backgroundColor: rootBgColor,
                    borderTopRightRadius: 2,
                    borderBottomRightRadius: 2,
                    height: 32,
                    margin: '6px 0',
                    padding: '0 6px',

                    selectors: splitButtonMenuButtonSelectors,
                },

                splitButtonDivider: {
                    color: rootColor,
                },

                splitButtonMenuButtonExpanded: {
                    color: pressedColor,
                    backgroundColor: rootExpandedBgColor,
                    selectors: splitButtonMenuButtonSelectors,
                },

                splitButtonContainer: {
                    margin: '0px 3px',
                },
            }}
        />
    );
});

function getColorSchemeFromProps(colorScheme: ColorScheme) {
    return {
        rootColor: getOptimalTextColorString(
            colorScheme.calLighterAlt,
            colorScheme,
            colorScheme.calDark
        ),
        pressedColor: getOptimalTextColorString(
            colorScheme.calLight,
            colorScheme,
            colorScheme.calDarker
        ),
        rootBgColor: colorScheme.calLighterAlt,
        rootPressedBgColor: colorScheme.calLight,
        rootExpandedBgColor: colorScheme.calLighter,
        rootHoveredBgColor: colorScheme.calLighter,
    };
}

function getColorSchemeFromTheme(themeSymbols: OwaPalette) {
    return {
        rootColor: themeSymbols.themeDark,
        pressedColor: themeSymbols.themeDarker,
        rootBgColor: themeSymbols.themeLighterAlt,
        rootHoveredBgColor: themeSymbols.themeLighter,
        rootExpandedBgColor: themeSymbols.themeLighter,
        rootPressedBgColor: themeSymbols.themeLight,
    };
}

export default FormsCommandBar;
